
import { message, Select } from 'antd';
import './user-model.less';
import { useContext, useEffect, useState } from 'react';
import { InputDropdown, Input, Modal } from 'bimui';
import { User } from '@/declare/interface/project/user';
import { changeUser, addUser, getUser, getUsers } from '@/declare/api/project/user';
import { addProjectUser, findProjectUsers } from '@/declare/api/project/project-user';
import { useParams } from 'react-router-dom';
import { ReactComponent as EditSvg } from 'bimui/dist/c1/icons/edit-white.svg';
import classNames from 'classnames';
import { RegisterStatus } from '@/store/register-store';
import { RegisterContext } from '@/App';
import CryptoJS from 'crypto-js'

/**
 * 弹窗组件的属性接口
 */
interface IProps {
    showModal: boolean; // 控制弹窗是否显示
    defaultUser: User | null; // 默认用户信息（可能为空）
    edit?: boolean; // 控制是否处于编辑状态是否显示
    onCancel?: () => void; // 取消操作的回调
    onNew?: (userNew: User) => void; // 新建保存操作的回调
    onEdit?: (selectedItems: User[]) => void; // 编辑保存操作的回调
    onClose?: () => void; // 弹窗关闭的回调
}
/**
 * 用户信息弹窗组件
 * 用于编辑、添加项目用户
 */
export default function UserModal(props: IProps) {
    const { showModal, defaultUser, onCancel, onNew, onEdit, onClose, edit } = props;
    const registerContext = useContext<RegisterStatus>(RegisterContext);
    /**
     * 从 URL 中提取项目 UUID 参数
     */
    const { projectUuid } = useParams();
    /**
     * 用户状态，用于存储当前操作的用户数据
     */
    const [userState, setUserState] = useState<User | null>();

    /**
     * 本地控制当前弹窗组件是处于编辑模式还是查看模式，父组件传递的edit用于判断弹窗初始处于哪种状态
     */
    const [isEditing, setIsEditing] = useState<boolean>(edit);
    /**
     * 标记是否通过编辑保存过
     */
    const [hasSaved, setHasSaved] = useState<boolean>(false);

    /**
     * 获取下拉选项数据
     */
    const [organizations, setOrganizations] = useState<{ label: string; value: string }[]>([]); // 组织下拉框的数据
    const [jobs, setJobs] = useState<{ label: string; value: string }[]>([]); // 职位下拉框的数据
    const [majors, setMajors] = useState<{ label: string; value: string }[]>([]); // 专业下拉框的数据


    /**
     * 初始化用户状态，监听 defaultUser 的变化（查看与编辑用户信息时使用）
     * 调用loadOptions将下拉选项数据处理后加载到本地
     */
    useEffect(() => {
        setUserState(defaultUser || null);
        loadOptions();
    }, [defaultUser]);

    /**
      * 加载下拉选项数据
      * 通过getUsers，findMajors获取系统用户列表与专业库列表
      * 获取去重后的组织、职位、专业（专业通过用户输入专业与专业库组合的数据处理而来）
      */
    const loadOptions = async () => {
        try {
            const [userRes] = await Promise.all([getUsers()]);
            const users = userRes.data || [];
            const majorData = registerContext.majors;

            // 获取去重后的组织、职位、专业
            const uniqueOrganizations = Array.from(new Set(users.map((u: User) => u.organization).filter(Boolean))).map(o => ({
                label: o as string,
                value: o as string
            })) as { label: string; value: string }[];

            const uniqueJobs = Array.from(new Set(users.map((u: User) => u.job).filter(Boolean))).map(j => ({
                label: j as string,
                value: j as string
            })) as { label: string; value: string }[];

            const userMajors = Array.from(new Set(users.map((u: User) => u.major).filter(Boolean)));

            // 合并专业库和用户专业
            const mergedMajors = Array.from(new Set([...majorData.map((m) => m.name), ...userMajors])).map(m => ({
                label: m as string,
                value: m as string
            })) as { label: string; value: string }[];

            setOrganizations(uniqueOrganizations);
            setJobs(uniqueJobs);
            setMajors(mergedMajors);
        } catch (error) {
            message.error('加载下拉选项失败');
        }
    };


    /**
     * 点击确认按钮，更新或新增用户信息的逻辑
     */
    const onSaveUser = () => {
        if (!userState?.name || userState?.name?.trim()?.length === 0) {
            message.warning("名字不能为空");
            return;
        }
        let param = {
            ...userState,
        };
        if (!param?.uuid) {

            if (!userState?.username || userState?.username?.trim()?.length === 0) {
                message.warning("用户名不能为空");
                return;
            }
            if (!userState?.password || userState?.password?.trim()?.length === 0) {
                message.warning("密码不能为空");
                return;
            }
            param.type = 2;
            // 新增
            param.password =  CryptoJS.MD5(param.password).toString();
            addUser(param).then(res => {
                if (res.status === 200) {
                    getUser(res.data).then(resUser => {
                        if (resUser.status === 200) {
                            onNew(resUser.data);
                            endAdd();
                        }
                    }).catch(err => {
                        message.error("获取用户信息失败");
                    });
                }
            }).catch(err => {
                message.error(err.data.message);
            });
        } else {
            // 编辑
            changeUser(param.uuid, param).then(res => {
                if (res.status === 200) {
                    setHasSaved(true); // 标记已保存
                    setIsEditing(false); // 切换回详情模式
                }
            }).catch(err => {
                message.error(err.data.message);
            })
        }
    }

    /**
     * 关于或取消的方法
     * 通过hasSaved判断是否保存过，如果保存过则获取最新用户信息传递给父组件，否则直接关闭
     */
    const handleClose = async () => {
        if (hasSaved) {
            // 保存后获取最新用户信息
            try {
                const res = await findProjectUsers(projectUuid);
                onEdit(res.data); // 传递最新用户数据
                endAdd();
            } catch (error) {
                message.error("获取最新用户数据失败");
            }
        }
        onCancel(); // 调用父组件的关闭回调
        endAdd();
    };

    /**
     * 动态生成标题内容
     */
    const getModalTitle = () => {
        if (!defaultUser?.uuid) {
            return "新建用户";
        } else if (isEditing) {
            return (
                <div className="modal-header-with-icon">
                    用户详情
                    <EditSvg className="modal-edit-icon" onClick={() => setIsEditing(false)} />
                </div>
            );
        } else {
            return (
                <div className="modal-header-with-icon">
                    用户详情
                    <EditSvg className="modal-edit-icon" onClick={() => setIsEditing(true)} />
                </div>
            );
        }
    };

    const endAdd = () => {
        setHasSaved(false);
        setIsEditing(false);
    };

    return (
        <Modal
            open={showModal}
            width={332}
            mask={false}
            title={getModalTitle()}
            onCancel={handleClose}
            onSave={onSaveUser}
            showFooter={!(defaultUser?.uuid && !isEditing)} // 查看详情时关闭底部按钮
            wrapClassName={classNames("user-modal-container", { userView: defaultUser?.uuid && !isEditing })}
        >
            <div className='user-row'>
                <div className={classNames("user-row-left", { edit: defaultUser })}>姓名</div>
                <div className={classNames("user-row-right", { edit: defaultUser })}>
                    {(!isEditing && defaultUser?.uuid) ? (
                        <span>{userState?.name || ""}</span> // 非编辑状态且有默认值时，显示纯文本
                    ) : (
                        <Input
                            style={{ width: '100%' }}
                            value={userState?.name || ""}
                            onChange={(e) => setUserState({ ...userState, name: e.target.value })}
                        />
                    )}
                </div>
            </div>
            <div className='user-row'>
                <div className={classNames("user-row-left", { edit: defaultUser })}>组织</div>
                <div className={classNames("user-row-right", { edit: defaultUser })}>
                    {(!isEditing && defaultUser?.uuid) ? (
                        <span>{userState?.organization || ""}</span> // 非编辑状态且有默认值时，显示纯文本
                    ) : (
                        <InputDropdown
                            options={organizations}
                            value={userState?.organization || ''}
                            placeholder="选择组织"
                            onChange={(value) => setUserState({ ...userState, organization: value })}
                            wrapClassName='user-model-dropdown'
                        />
                    )}
                </div>
            </div>
            <div className='user-row'>
                <div className={classNames("user-row-left", { edit: defaultUser })}>部门</div>
                <div className={classNames("user-row-right", { edit: defaultUser })}>
                    {(!isEditing && defaultUser?.uuid) ? (
                        <span>{userState?.department || ""}</span> // 非编辑状态且有默认值时，显示纯文本
                    ) : (
                        <Input
                            style={{ width: '100%' }}
                            value={userState?.department || ""}
                            onChange={(e) => setUserState({ ...userState, department: e.target.value })}
                        />
                    )}
                </div>
            </div>
            <div className='user-row'>
                <div className={classNames("user-row-left", { edit: defaultUser })}>职位</div>
                <div className={classNames("user-row-right", { edit: defaultUser })}>
                    {(!isEditing && defaultUser?.uuid) ? (
                        <span>{userState?.job || ""}</span> // 非编辑状态且有默认值时，显示纯文本
                    ) : (
                        <InputDropdown
                            options={jobs}
                            value={userState?.job || ''}
                            placeholder="选择职位"
                            onChange={(value) => setUserState({ ...userState, job: value })}
                            wrapClassName='user-model-dropdown'
                        />
                    )}
                </div>
            </div>
            <div className='user-row'>
                <div className={classNames("user-row-left", { edit: defaultUser })}>专业</div>
                <div className={classNames("user-row-right", { edit: defaultUser })}>
                    {(!isEditing && defaultUser?.uuid) ? (
                        <span>{userState?.major || ""}</span> // 非编辑状态且有默认值时，显示纯文本
                    ) : (
                        <InputDropdown
                            options={majors}
                            value={userState?.major || ''}
                            placeholder="选择专业"
                            onChange={(value) => setUserState({ ...userState, major: value })}
                            wrapClassName='user-model-dropdown'
                        />
                    )}
                </div>
            </div>
            <div className='user-row'>
                <div className={classNames("user-row-left", { edit: defaultUser })}>电话</div>
                <div className={classNames("user-row-right", { edit: defaultUser })}>
                    {(!isEditing && defaultUser?.uuid) ? (
                        <span>{userState?.mobile || ""}</span> // 非编辑状态且有默认值时，显示纯文本
                    ) : (
                        <Input
                            style={{ width: '100%' }}
                            value={userState?.mobile || ""}
                            onChange={(e) => setUserState({ ...userState, mobile: e.target.value })}
                        />
                    )}
                </div>
            </div>
            {/* 仅当是新增用户时显示用户名与密码的输入框 */}
            {!defaultUser?.uuid && (
                <>
                    <div className='user-row'>
                        <div className="user-row-left">用户名</div>
                        <div className="user-row-right">
                            <Input
                                style={{ width: '100%' }}
                                value={userState?.username || ""}
                                onChange={(e) => setUserState({ ...userState, username: e.target.value })}
                            />
                        </div>
                    </div>
                    <div className='user-row'>
                        <div className="user-row-left">密码</div>
                        <div className="user-row-right">
                            <Input
                                style={{ width: '100%' }}
                                value={userState?.password || ""}
                                onChange={(e) => setUserState({ ...userState, password: e.target.value })}
                            />
                        </div>
                    </div>
                </>
            )}
        </Modal >
    )
}